import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCheck } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import type { AlertRecord } from "../shared/schemas";
import { EmptyState, ErrorState, LoadingState, PageHeader, PriorityBadge, StatusBadge } from "../components/ui";

export function AlertsPage() {
  const { snapshotTermId, departmentId, user } = useAppContext();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["alerts", snapshotTermId, departmentId],
    queryFn: () => apiFetch<AlertRecord[]>(`/alerts${queryString({ snapshotTermId, department: departmentId })}`),
    enabled: Boolean(snapshotTermId),
  });
  const mutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "acknowledged" | "resolved" }) =>
      apiFetch(`/alerts/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["alerts", snapshotTermId] }),
  });
  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;
  const canWrite = user.role !== "faculty";
  return (
    <>
      <PageHeader title="Alert Center" description="Persistent operational alerts with acknowledgement and resolution audit history." />
      {query.data?.length === 0 ? (
        <EmptyState title="No alerts" description="No conflicts, capacity hazards, utilization flags, or waitlist bottlenecks were generated." />
      ) : (
        <div className="panel divide-y divide-slate-100">
          {query.data?.map((alert) => (
            <article key={alert.id} className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2"><PriorityBadge priority={alert.priority} /><StatusBadge status={alert.status} /><span className="text-xs font-semibold text-slate-400">{alert.alertType.replaceAll("_", " ")}</span></div>
                <h2 className="mt-2 font-bold text-slate-900">{alert.title}</h2>
                <p className="mt-1 text-sm text-slate-500">{alert.message}</p>
              </div>
              {canWrite && alert.status !== "resolved" && (
                <div className="flex gap-2">
                  {alert.status === "open" && <button className="button-secondary" onClick={() => mutation.mutate({ id: alert.id, status: "acknowledged" })}><Check className="h-4 w-4" /> Acknowledge</button>}
                  <button className="button-primary" onClick={() => mutation.mutate({ id: alert.id, status: "resolved" })}><CheckCheck className="h-4 w-4" /> Resolve</button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </>
  );
}
